import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import Task from '@src/slide/infrastructure/module/dependent/taskManager/task';
import TaskManager, { TaskPriority } from '@src/slide/infrastructure/module/dependent/taskManager';
import { IGroupModel } from '@src/slide/domain/ui-model/element-modeles/Interface/group-model.interface';
import { ISmartArtModel } from '@src/slide/domain/ui-model/element-modeles/Interface/smart-art-model.interface';
import { Container } from '@tencent/alloy-renderer';
import { RendererGroupComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-group-component';
import { RendererSmartArtComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-smart-art-component';
import { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import TaskGroup from '@src/slide/infrastructure/module/dependent/taskManager/taskGroup';
import { ISlideComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/slide/types';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { RenderPostionType } from '@src/js/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { CANVAS_SMART_ART_ID_PREFIX } from '@src/slide/content-viewer/service/canvas-renderer/core/id-constants';

interface IServiceDelegate {
    selectionService?: ISelectionService;
}

export class BuildTreeAsync {
    public readonly taskGroupKey: string;
    private firstBuildTaskGroup: TaskGroup;
    private currentToBuildTaskNeedInfo: Array<IElementBaseModel> = [];
    private currentNum = 0;
    private groupNodeMap = new Map<string, ComponentFactory>();
    // 组合里的元素加入队列
    private groupTaskQueue: Array<IGroupModel> = [];
    private smartArtTaskQueue: Array<ISmartArtModel> = [];
    private taskNum = 0;
    private slideComponent: ISlideComponent;
    private slideModel: ISlideModel;
    private elementsLayer: Container;
    private selectionService?: ISelectionService;

    public constructor(
        slideComponent: ISlideComponent,
        elementsLayer: Container,
        taskGroupKey: string,
        serviceDelegate: IServiceDelegate,
    ) {
        this.slideComponent = slideComponent;
        this.elementsLayer = elementsLayer;
        this.slideModel = slideComponent.options.slideModel;
        this.taskGroupKey = taskGroupKey;
        const {
            selectionService,
        } = serviceDelegate;
        this.selectionService = selectionService;
    }

    public async asyncBuildTree(): Promise<any> {
        this.firstBuildTaskGroup = new TaskGroup(this.taskGroupKey, this.getTaskPriority());
        this.firstBuildTaskGroup.concurrentMax = 1;
        // 拆分任务去创建子树
        this.splitSlideChildren();
        return TaskManager.addTask(this.firstBuildTaskGroup);
    }

    public cancelBuild(): void {
        this.elementsLayer?.dispose?.();
        if (TaskManager.getTaskWithKey(this.taskGroupKey)) {
            TaskManager.cancelTaskByKey(this.taskGroupKey);
        }
    }

    private splitSlideChildren() {
        this.slideModel.children.forEach((model: IElementBaseModel) => {
            if (model.type === UIModelType.TABLE) {
                // 表格的单独拆分一个任务
                if (this.currentNum > 0) {
                    // table前面有已经拆分好的任务需要先添加到任务队列里
                    this.addBuildTask(this.currentToBuildTaskNeedInfo, this.slideModel.key, false);
                    this.resetTaskInfo();
                }
                // table单独一个task
                const task = new Task(this.generateSingleTaskKey(), TaskPriority.HIGH, () => {
                    this.buildElement(model, this.elementsLayer, false);
                });
                this.firstBuildTaskGroup.addTask(task);
            } else {
                if (model.type === UIModelType.GROUP) {
                    // 碰到group要添加到任务队列
                    this.groupTaskQueue.push(model as IGroupModel);
                }
                if (model.type === UIModelType.SMART_ART) {
                    // 碰到smart art要添加到任务队列
                    this.smartArtTaskQueue.push(model as ISmartArtModel);
                }
                if (this.currentNum < this.getElementCountPerTask()) {
                    this.currentNum += 1;
                    this.currentToBuildTaskNeedInfo.push(model);
                }
                if (this.currentNum === this.getElementCountPerTask()) {
                    this.addBuildTask(this.currentToBuildTaskNeedInfo, this.slideModel.key, false);
                    this.resetTaskInfo();
                }
            }
        });
        if (this.currentNum > 0) {
            // table前面有已经拆分好的任务需要先添加到任务队列里
            this.addBuildTask(this.currentToBuildTaskNeedInfo, this.slideComponent.options.slideModel.key, false);
            this.resetTaskInfo();
        }
        this.splitGroupQueue();
    }

    // 依次处理组合队列中的子元素进行分组
    private splitGroupQueue() {
        while (this.groupTaskQueue.length > 0) {
            const current = this.groupTaskQueue.shift();
            current && this.splitGroupChildren(current);
            this.resetTaskInfo();
        }
        while (this.smartArtTaskQueue.length > 0) {
            const current = this.smartArtTaskQueue.shift();
            current && this.splitSmartArtChildren(current);
            this.resetTaskInfo();
        }
    }

    private splitGroupChildren(groupModel: IGroupModel) {
        groupModel.children.forEach((model: IElementBaseModel) => {
            if (model.type === UIModelType.GROUP) {
                // 碰到group要添加到任务队列
                this.groupTaskQueue.push(model as IGroupModel);
            }
            if (this.currentNum < this.getElementCountPerTask()) {
                this.currentNum += 1;
                this.currentToBuildTaskNeedInfo.push(model);
            }
            if (this.currentNum === this.getElementCountPerTask()) {
                this.addBuildTask(this.currentToBuildTaskNeedInfo, groupModel.key, true);
                this.resetTaskInfo();
            }
        });
        if (this.currentNum > 0) {
            // table前面有已经拆分好的任务需要先添加到任务队列里
            this.addBuildTask(this.currentToBuildTaskNeedInfo, groupModel.key, true);
            this.resetTaskInfo();
        }
    }

    private splitSmartArtChildren(smartArtModel: ISmartArtModel) {
        smartArtModel.shapeList.forEach((model: IElementBaseModel) => {
            if (this.currentNum < this.getElementCountPerTask()) {
                this.currentNum += 1;
                this.currentToBuildTaskNeedInfo.push(model);
            }
            if (this.currentNum === this.getElementCountPerTask()) {
                this.addBuildTask(this.currentToBuildTaskNeedInfo, smartArtModel.key, true);
                this.resetTaskInfo();
            }
        });
        if (this.currentNum > 0) {
            // table前面有已经拆分好的任务需要先添加到任务队列里
            this.addBuildTask(this.currentToBuildTaskNeedInfo, smartArtModel.key, true);
            this.resetTaskInfo();
        }
    }

    private resetTaskInfo() {
        this.currentToBuildTaskNeedInfo = [];
        this.currentNum = 0;
    }

    /**
     * 每个task的任务
     * @param elementModelArray
     * @param parentNodeKey
     * @param isInGroup
     * @private
     */
    private addBuildTask(elementModelArray: Array<any>, parentNodeKey: string, isInGroup: boolean) {
        // TODO: task的key要设置成合适的
        const task = new Task(this.generateSingleTaskKey(), TaskPriority.HIGH, () => {
            for (let i = 0; i < elementModelArray.length; i++) {
                const elementModel = elementModelArray[i];
                let parentNode;
                if (!isInGroup) {
                    parentNode = this.elementsLayer;
                    this.buildElement(elementModel, parentNode, isInGroup);
                } else {
                    parentNode = this.groupNodeMap.get(parentNodeKey);
                    parentNode && this.buildElement(elementModel, parentNode.inner, isInGroup);
                }
            }
        });
        this.firstBuildTaskGroup.addTask(task);
    }

    private generateSingleTaskKey(): string {
        const taskKey =  `build-${this.slideComponent.options.region}-${this.slideModel.key}-${this.taskNum}`;
        this.taskNum += 1;
        return taskKey;
    }

    private childFactoryOption(childModel): InterfaceFactoryOptions {
        return {
            model: childModel,
            isMasterEdit: this.slideComponent.options.isMasterEdit,
            getPageScale: this.slideComponent.options.getPageScale,
            getApplication: this.slideComponent.options.getApplication,
            region: this.slideComponent.options.region,
            isCopyToImage: this.slideComponent.options.isCopyToImage,
            fromScale: this.slideComponent.options.fromScale,
            logicService: this.slideComponent.options.logicService,
            getFilterDisplayOptions: this.slideComponent.getFilterDisplayOptions,
            getPlayerReduxStore: this.slideComponent.options.getPlayerReduxStore,
            handleSlideClick: this.slideComponent.options.handleSlideClick,
            mobileEditMode: this.slideComponent.options.mobileEditMode,
            slideModel: this.slideModel,
            selectionService: this.selectionService,
            uiDataService: this.slideComponent.getUiDataServiceDelegate(),
            mediaManager: this.slideComponent.getMediaManagerDelegate(),
            rightSideBarManager: this.slideComponent.getRightSideBarManager(),
            addonManager: this.slideComponent.getAddonManager(),
            attachmentManager: this.slideComponent.getAttachmentManager(),
            editorAnimPreviewManager: this.slideComponent.getEditorAnimPreviewManager(),
            editingStateManager: this.slideComponent.getEditingStateManager(),
            formatBrushManager: this.slideComponent.getFormatBrushManager(),
            refreshBackground: this.slideComponent.refreshBackground,
            rightSideBarService: this.slideComponent.getRightSideBarService(),
            infrastructureService: this.slideComponent.options.infrastructureService,
            previewService: this.slideComponent.options.previewService,
            textEditorStateService: this.slideComponent.options.textEditorStateService,
            historyManager: this.slideComponent.options.historyManager,
            copyPasteImageService: this.slideComponent.options.copyPasteImageService,
            masterLayoutEditService: this.slideComponent.options.masterLayoutEditService,
            fontManager: this.slideComponent.options.fontManager,
            linkManager: this.slideComponent.options.linkManager,
            accessibilityManager: this.slideComponent.options.accessibilityManager,
            undoRedoService: this.slideComponent.options?.undoRedoService,
            textSelectionUndoRedo: this.slideComponent.options?.textSelectionUndoRedo,
            editAreaCanvasSizeManager: this.slideComponent.options?.editAreaCanvasSizeManager,
            gifService: this.slideComponent.options?.gifService,
        };
    }

    /**
     * 创建group或者slide的单个子元素
     * @param elementModel
     * @param toAddParent
     * @param isInGroup
     * @private
     */
    private buildElement(
        elementModel: any,
        toAddParent: Container | RendererGroupComponent | RendererSmartArtComponent,
        isInGroup: boolean,
    ): void {
        try {
            let factoryOption;
            if (isInGroup) {
                if (toAddParent.id.indexOf(CANVAS_SMART_ART_ID_PREFIX) >= 0) {
                    factoryOption = (toAddParent as RendererSmartArtComponent).childFactoryOption(elementModel);
                } else {
                    factoryOption = (toAddParent as RendererGroupComponent).childFactoryOption(elementModel);
                }
            } else {
                factoryOption = this.childFactoryOption(elementModel);
            }
            const childFactory = new ComponentFactory({
                ...factoryOption,
                asyncBuild: true });
            toAddParent.addChild(childFactory.getWrapper());
            childFactory.factoryOptions.asyncBuild = false;
            if ((elementModel.type === UIModelType.GROUP || elementModel.type === UIModelType.SMART_ART)
            && !this.groupNodeMap.has(elementModel.key)) {
                this.groupNodeMap.set(elementModel.key, childFactory);
            }
        } catch (e) {
            window.log.error(`[canvas-async-build-tree]:${e}`);
            tdw({
                opername: TDWOperationType.PPT_QUALITY,
                module: TDWModuleType.CANVAS_RENDERER,
                action: TDWActionType.CANVAS_RENDERER_BUILD_TREE,
                ver6: JSON.stringify({ errorMsg: e.message, async: 1 }),
            });
        }
    }

    /**
     * 根据区域的不同设置不同的优先级
     * @private
     */
    private getTaskPriority(): number {
        const { region } = this.slideComponent.options;
        if (region === RenderPostionType.LEFTLIST
            || region === RenderPostionType.TEMPLATE) return TaskPriority.HIGH;
        return TaskPriority.NORMAL;
    }

    /**
     * 创建渲染树的过程中的每个Task控制的数量
     */
    private getElementCountPerTask(): number {
        const { region } = this.slideComponent.options;
        if (region === RenderPostionType.PLAYER) return 2;
        return 5;
    }
}
